import { PricingType, PricingTypes } from '../../../common/constants'
import { CompletedWorkareaVisit } from "../../../domain/invoicing/invoicingApi"
import { WorkareaGroupWithWorkareas } from "../../../domain/workareas/group"
import { Transaction } from "../../../domain/invoicing/transaction"
import { WorkareaSummary } from "../../Items/itemSummaryModel"
import { sum } from "../../../helpers/array"
import { hasValue } from "../../../helpers/nullable"
import { InvoiceSummary } from "../../../domain/invoicing/invoice"

export type WorkareaGroupTransactionTotals = {
  id: string
  name: string
  pricingType: PricingType
  isApproved: boolean
  completedVisits: number
  estimatedHours: number
  actualHours: number
  estimatedPrice: number
  actualPrice: number
  showTaxInFinalPrice: boolean
  invoicedAmount: number
  billableAmount: number
}

export const defaultWorkareaGroupTransactionTotals = (): WorkareaGroupTransactionTotals => ({
  id: '',
  name: '',
  pricingType: PricingTypes.TimeAndMaterials,
  isApproved: false,
  completedVisits: 0,
  estimatedHours: 0,
  actualHours: 0,
  estimatedPrice: 0,
  actualPrice: 0,
  showTaxInFinalPrice: false,
  invoicedAmount: 0,
  billableAmount: 0,
})

export type WorkareaGroupInvoiceSummary = {
  id: string
  name: string
  pricingType: PricingType
  isApproved: boolean
  completedVisits: number
  estimatedHours: number
  actualHours: number
  estimatedPrice: number
  actualPrice: number
  showTaxInFinalPrice: boolean
  invoicedAmount: number
  billableAmount: number
}

export const buildWorkareaGroupInvoiceTotals = (
  workareaGroups: WorkareaGroupWithWorkareas[],
  workareaSummaries: WorkareaSummary[],
  completedWorkareaVisits: CompletedWorkareaVisit[],
  transactions: Transaction[]
): WorkareaGroupInvoiceSummary[] => {
  return workareaGroups.map(group => {
    const workareaIds = group.workareas.map(x => x.id)
    const summaries = workareaSummaries.filter(x => workareaIds.includes(x.workareaId))
    const actualPrice = sum(summaries, x => x.finalPrice)
    const elements = transactions.filter(x => x.status === 'committed')
      .flatMap(x => x.elements)
      .filter(x => hasValue(x.workareaId) && workareaIds.includes(x.workareaId))
    const invoicedAmount = sum(elements, x => x.amount)

    return {
      id: group.id,
      name: group.name,
      pricingType: group.pricingType,
      isApproved: group.isApproved,
      completedVisits: completedWorkareaVisits.filter(x => workareaIds.includes(x.workareaId)).length,
      estimatedHours: sum(summaries, x => x.estimatedHours),
      actualHours: sum(summaries, x => x.actualHours),
      estimatedPrice: sum(summaries, x => x.estimatedPrice),
      actualPrice,
      showTaxInFinalPrice: group.showTaxInFinalPrice,
      invoicedAmount,
      billableAmount: (group.isApproved) ? actualPrice : invoicedAmount,
    }
  })
}

export type InvoiceTotals = {
  totalBillable: number,
  notInvoiced: number,
  outstanding: number,
  paid: number
}

export const calculateInvoiceTotals = (summaries: WorkareaGroupInvoiceSummary[], invoices: InvoiceSummary[]): InvoiceTotals => {
  const totalBillable = sum(summaries, x => x.billableAmount)
  const invoiceTotalWithoutTax = sum(invoices, x => x.amount)
  const outstanding = sum(invoices, x => x.balance)

  return {
    totalBillable,
    notInvoiced: totalBillable - invoiceTotalWithoutTax,
    outstanding,
    paid: invoiceTotalWithoutTax + sum(invoices, x => x.salesTax) - outstanding
  }
}